import {
  skipToken,
  type DefaultError,
  type Enabled,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { QueryOperation } from './apiComponents';
import { getAccessToken, getHighestRole } from 'business/user/services/authentication';
import { HASURA_ROLE_KEY } from 'technical/auth/hasura/constants';

export type ApiContext<
  TQueryFnData = unknown,
  _TError = DefaultError,
  _TData = TQueryFnData,
  _TQueryKey extends QueryKey = QueryKey,
> = {
  fetcherOptions: {
    /**
     * Headers to inject in the fetcher
     */
    headers?: {};
    /**
     * Query params to inject in the fetcher
     */
    queryParams?: {};
  };
  queryOptions: {
    /**
     * Set this to `false` to disable automatic refetching when the query mounts or changes query keys.
     * Defaults to `true`.
     */
    enabled?: boolean
    // The default type is the following but it gives an error when used on other places
    // enabled?: Enabled<TQueryFnData, TError, TQueryFnData, TQueryKey>;
  };
};

/**
 * Context injected into every react-query hook wrappers
 *
 * @param queryOptions options from the useQuery wrapper
 */
export function useApiContext<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  _queryOptions?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn'
  >,
): ApiContext {
  const headers: Record<string, string> = {};

  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers[HASURA_ROLE_KEY] = getHighestRole();
  }

  return {
    fetcherOptions: {
      headers
    },
    queryOptions: {},
  };
}

export const queryKeyFn = (operation: QueryOperation): QueryKey => {
  const queryKey: unknown[] = hasPathParams(operation)
    ? operation.path
        .split('/')
        .filter(Boolean)
        .map((i) => resolvePathParam(i, operation.variables.pathParams))
    : operation.path.split('/').filter(Boolean);

  if (hasQueryParams(operation)) {
    queryKey.push(operation.variables.queryParams);
  }

  if (hasBody(operation)) {
    queryKey.push(operation.variables.body);
  }

  return queryKey;
};

// Helpers
const resolvePathParam = (key: string, pathParams: Record<string, string>) => {
  if (key.startsWith('{') && key.endsWith('}')) {
    return pathParams[key.slice(1, -1)];
  }
  return key;
};

const hasPathParams = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { pathParams: Record<string, string> };
} => {
  if (operation.variables === skipToken) return false;
  return 'variables' in operation && 'pathParams' in operation.variables;
};

const hasBody = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { body: Record<string, unknown> };
} => {
  if (operation.variables === skipToken) return false;
  return 'variables' in operation && 'body' in operation.variables;
};

const hasQueryParams = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { queryParams: Record<string, unknown> };
} => {
  if (operation.variables === skipToken) return false;
  return 'variables' in operation && 'queryParams' in operation.variables;
};
